import { mapMutations, mapState } from 'vuex';
import storefront from 'lib/storefront';
import { decodeObjectID } from 'utils/general';
import {
  trackProductFindShade,
  trackTryOn,
  trackTryShadeOn,
  trackFindMyShade,
} from 'lib/tracking';
import * as queries from 'lib/graphql/queries';

export default {
  data() {
    return {
      parentProductId: null,
      campaignType: 'solo',
      showingCombo: false,
      isTryCombination: false,
      myCustomValue: true,
    };
  },
  computed: {
    ...mapState('product', ['product', 'metafields', 'showingTryShade', 'loading']),
    showFindShadeButton() {
      if (!this.metafields) {
        return false;
      }
      return ![true, 'true'].includes(
        this.metafields?.general?.hide_shade_buttons,
      );
    },
    showTryShadeOnButton() {
      if (!this.metafields || window.__GLOBAL__.disableVto) {
        return false;
      }
      return ![true, 'true'].includes(
        this.metafields?.general?.hide_try_shade_on_button,
      );
    },
    isShadowStix() {
      return this.product.tags?.includes('Shadowstix');
    },
    showGetTheLookButton() {
      return document.querySelector('.js-get-the-look') != null;
    }
  },
  created() {
    const isMobile = screen.width <= 414;
    const width = isMobile ? screen.width - 20 : 600;
    const height = isMobile ? parseInt((screen.width - 20) * 1.4) : 800;

    window.ymkAsyncInit = () => {
      window.YMK.init({
        width: width,
        height: height
      });

      window.YMK.addEventListener('loaded', async () => {
        if (!this.parentProductId) {
          await this.getParentProductId();
        }
        YMK.applyMakeupBySku(this.parentProductId);
      });

      window.YMK.addEventListener('closed', () => {
        this.setShowingTryShade(false)
        trackTryShadeOn({
          action: 'try_shade_on_close',
          shopify_module_name: 'product shade action buttons',
        });
        this.showingCombo = false;
        this.campaignType = 'solo';
        this.isTryCombination = true;
      });

      this.bindTracking();

      const params = new URLSearchParams(window.location.search);
      if (params.get('try-shade') === 'true') {
        this.toggleTryShadeModule();
      }
    };

    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.src = window.__GLOBAL__.shadeFinderUrl;
    const x = document.getElementsByTagName('script')[0];
    x.parentNode.insertBefore(s, x);
  },
  methods: {
    ...mapMutations('product', ['setShowingTryShade']),
    toggleSoloCombination(type) {
      this.campaignType = type;
      if (this.isShadowStix) {
        if (type === 'solo') {
          if (window.YMK.isLoaded()) {
            window.YMK.reset();
            window.YMK.applyMakeupBySku(this.product.id);
          } else {
            window.YMK.applyMakeupBySku(this.product.id);
            window.YMK.open({ loadLiveCam: true });
          }

          trackTryOn(this.product);
          trackTryShadeOn({
            action: 'try_shade_on_open',
            shopify_module_name: 'product shade action buttons',
          });
          this.showingCombo = false;
          this.isTryCombination = false;
          this.setLeftShadeButton(this.product.id);
        } else {
          this.setTryCombination();
        }
        this.setShowingTryShade(true)
      }
    },
    async setLeftShadeButton(id) {
      const { product } = await this.getShadowstixProductById(id);

      if (product) {
        const colorIdTag = product.tags?.find((t) => t.includes('Color ID'));
        if (colorIdTag) {
          const colorId = colorIdTag?.split('|')[1];
          const shade = this.shades.find((shade) => shade.id === colorId);
          this.circleBackgroundColor1 = shade?.hex;
        }
        this.tagText = product?.shadeDetails?.value;
        this.productTitle = product?.variants.edges[0]?.node.title;
      }
    },
    toggleTryShadeModule() {
      this.setShowingTryShade(!this.showingTryShade)

      if (this.showingTryShade) {
        const item = this.product;
        window.klaviyo.push(['track', 'Try shade product', item]);
        if (this.isShadowStix) {
          this.toggleSoloCombination('solo');
          this.isTryCombination = false;
        } else {
          window.YMK.open({ loadLiveCam: true });
        }
        trackTryOn(this.product);
        trackTryShadeOn({
          action: 'try_shade_on_open',
          shopify_module_name: 'product shade action buttons',
        });
      } else {
        window.YMK.close();
        this.showingCombo = false;
      }
    },
    bindTracking() {
      window.YMK.addEventListener('reset', () => {
        trackTryShadeOn({
          action: 'try_shade_on_reset',
          shopify_module_name: 'product shade action buttons',
        });
      });

      window.YMK.addEventListener('compare', () => {
        trackTryShadeOn({
          action: 'try_shade_on_compare',
          shopify_module_name: 'product shade action buttons',
        });
      });

      window.YMK.addEventListener('compareDisabled', () => {
        trackTryShadeOn({
          action: 'try_shade_on_compare_disable',
          shopify_module_name: 'product shade action buttons',
        });
      });

      window.YMK.addEventListener('photoSaved', () => {
        trackTryShadeOn({
          action: 'try_shade_on_save_photo',
          shopify_module_name: 'product shade action buttons',
        });
      });
    },
    async getShadowstixProductById(id) {
      const response = await storefront.request(
        queries.getShadowstixProductById,
        {
          id: 'gid://shopify/Product/' + id,
        },
      );
      return response;
    },
    async getParentProductId() {
      if (!Object.hasOwn(this.product, 'title')) return;
      // If the product is a mini, try to query for the parent/full size product
      if (this.product.title.toLowerCase().includes('mini')) {
        const productHandle = this.product.handle.replace('-mini', '');
        const response = await storefront.request(queries.product, {
          handle: productHandle,
        });
        if (response?.product?.id) {
          this.parentProductId = decodeObjectID(response.product.id);
        } else {
          this.parentProductId = this.product.id;
        }
      } else {
        this.parentProductId = this.product.id;
      }
    },
    goToShadeFinder() {
      trackFindMyShade({ action: 'find_my_shade_initiate', shopify_module_name: 'product shade action buttons' });
      trackProductFindShade(this.product);
      const item = this.product;
      window.klaviyo.push(['track', 'Shade finder recommended product', item]);
      location.href = __GLOBAL__.shades.finderHandle;
    },
    scrollToGetTheLook() {
      const getTheLookSection = document.querySelector('.js-get-the-look')
      if (getTheLookSection) {
        const elementPosition = getTheLookSection.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.scrollY - 100;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
      }
    }
  },
};
